import { Button } from "@mui/material";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    useProjectAndSubmissions,
    useProjecUpdate,
} from "../../../hooks/useProject";
import { useProjectShareholderTally } from "../../../hooks/useShareholder";
import transl from "../../components/translate";
import Header from "../components/Header";
import SingleProjectInfo from "./components/SingleProjectInfo";
import SingleProjectResults from "./components/SingleProjectResults";
import SingleProjectShareholders from "./components/SingleProjectShareholders";
import ProjectTitle from "./components/projectTitle";

function SingleProjectIndex() {
    const { id } = useParams();
    const updateProjectMutation = useProjecUpdate();
    const { data: project, isLoading: isProjectsLoading } =
        useProjectAndSubmissions(id);

    // Keyed on the route param, not on project.id from the query above, so it
    // runs in PARALLEL with it. Previously an effect set the shareholder query's
    // id only after the project resolved, serializing two full-table reads.
    //
    // This replaces downloading every shareholder row: the results panel only
    // ever needed per-result share totals, which Postgres aggregates in ~140ms.
    const { data: tally, isLoading: isTallyLoading } =
        useProjectShareholderTally(id);

    const handlePusblishProject = (status) => {
        //create a alert to confirm the action
        if (
            window.confirm(`Are you sure you want to ${status} this project?`)
        ) {
            updateProjectMutation.mutate(
                {
                    project_id: id,
                    meta: { status: status },
                },
                {
                    onSuccess: () => {
                        toast.success(
                            transl("Status Updated to the current project"),
                            {
                                position: "top-right",
                                autoClose: 4000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                                progress: undefined,
                            },
                        );
                    },
                },
            );
        }
    };

    return (
        <>
            {isProjectsLoading ? (
                <p>{transl("Loading")}</p>
            ) : !project ? (
                <p>{transl("Project Unavailable")}</p>
            ) : (
                <div>
                    <Header
                        title={
                            <ProjectTitle
                                title={project.title}
                                status={project.status}
                                project_id={project.id}
                            />
                        }
                    >
                        <Button sx={{ marginRight: "20px" }}>
                            <Link to={`/dashboard/resources/${project.id}`}>
                                {transl("Project resources")}
                            </Link>
                        </Button>
                        {project.status == "draft" && (
                            <Button
                                variant="contained"
                                onClick={() => {
                                    handlePusblishProject("publish");
                                }}
                            >
                                {transl("Publish Project")}
                            </Button>
                        )}
                        {project.status == "publish" && (
                            <>
                                <Button
                                    sx={{ marginRight: "20px" }}
                                    variant="outlined"
                                    onClick={() => {
                                        handlePusblishProject("deleted");
                                    }}
                                >
                                    {transl("Delete Project")}
                                </Button>
                                <Button
                                    sx={{ marginRight: "20px" }}
                                    variant="contained"
                                    onClick={() => {
                                        handlePusblishProject("draft");
                                    }}
                                >
                                    {transl("pause project")}
                                </Button>
                            </>
                        )}
                        <Button>
                            <Link
                                to={`/dashboard/activity-report/new/${project.id}`}
                            >
                                {transl("Add New Submission")}
                            </Link>
                        </Button>
                    </Header>
                    <SingleProjectInfo
                        title={project.title}
                        startDate={project?.start_date}
                        endDate={project?.end_date}
                        status={project.status}
                        project={project}
                        hasSubmission={project?.submission?.length}
                    />
                    <SingleProjectResults
                        project={project}
                        results={project.results}
                        tally={tally}
                        isTallyLoading={isTallyLoading}
                    />
                    {/* Not gated on the tally: this table pages its own rows
                        server-side, so it can render while the totals load. */}
                    <SingleProjectShareholders
                        project={project}
                        shareholderCount={tally?.shareholderCount}
                    />
                </div>
            )}
        </>
    );
}

export default SingleProjectIndex;
